// driver-application-form.component.ts
import {
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpClient, HttpParams } from '@angular/common/http';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { MatSliderModule } from '@angular/material/slider';
import { RouterLink } from '@angular/router';
import { catchError, merge, of } from 'rxjs';
import { JobPositionPublicDto } from '@/app/models/public/jobPosition.public.dto';
import { JobSearchResultPublicDto } from '@/app/models/public/jobSearchResult.public.dto';
import { MatchedLeadsProductPublicDto } from '@/app/models/public/matchedLeadsProduct.public.dto';
import { JobSearchCardComponent } from '../../jobs/job-search-card/job-search-card.component';
import { TdusaPartnerLead } from '@/app/models/admin/TdusaPartnerLead';
import { TdusaPartnerLeadResponse } from '@/app/models/admin/TdusaPartnerLeadResponse';
import { HomePartialLeadDto } from '@/app/models/homePartialLead.dto';
import { CdaiDownloadComponent } from './cdai-download/cdai-download.component';
import {
  PhoneFormatters,
  usPhoneE164Validator,
} from '@/app/formatters/phone.formatters';
import { debounceTime } from 'rxjs/operators';
import { DriverApplicationModalComponent } from '../driver-application-modal/driver-application-modal.component';


const CDL_CLASSES = ['Class A', 'Class B', 'Class C'] as const;
const ENDORSEMENTS = [
  'H - Hazardous Materials',
  'N - Tanker Vehicles',
  'T - Doubles or Triples',
  'P - Passengers',
  'S - School Bus',
  'X - Combo for Hazmat and Tank Vehicles',
  'TWIC - TWIC Card',
] as const;
const EXPERIENCE_OPTIONS = [
  'Need CDL Training',
  'In CDL School Now',
  'CDL Grad - No Experience Yet',
  '1-5 Months',
  '6-11 Months',
  '12-23 Months',
  '2 Years',
  '3 Years',
  '4 Years',
  '5+ Years',
] as const;
const FREIGHT_TYPES = [
  'Dry Van',
  'Refrigerated (Reefer)',
  'Flatbed',
  'Tanker',
  'Doubles / Triples',
  'Intermodal',
  'Household Goods',
  'Hazmat',
  'Specialized',
  'Auto Hauler',
  'Double Pups',
  'Dry Bulk / Pneumatic',
] as const;
const CONTACT_TIMES = ['Anytime', 'Morning', 'Afternoon', 'Evening'] as const;

@Component({
  selector: 'app-driver-application-form',
  templateUrl: './driver-application-form.component.html',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterLink,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    MatRadioModule,
    MatSliderModule,
    MatButtonModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    JobSearchCardComponent,
    CdaiDownloadComponent,
  ],
  providers: [],
})
export class DriverApplicationFormComponent implements OnInit {
  // Optional inputs
  jobPositionId = input<string | undefined>(undefined);
  matchedLeadsProduct = input<MatchedLeadsProductPublicDto | undefined>(
    undefined
  );
  modal = input<DriverApplicationModalComponent>(undefined)

  // Expose constants to template
  readonly cdlClasses = CDL_CLASSES;
  readonly endorsements = ENDORSEMENTS;
  readonly experienceOptions = EXPERIENCE_OPTIONS;
  readonly freightTypes = FREIGHT_TYPES;
  readonly contactTimes = CONTACT_TIMES;

  // State
  submitting = signal(false);
  submitted = signal(false);
  disqualified = signal(false);
  disqualifiedMessage = signal<string>('');
  qualifyingJobs = signal<JobPositionPublicDto[]>([]);
  loadingJobs = signal(false);

  // Matched application offer (shown when a company/job direct apply doesn't qualify)
  matching = signal(false);
  matched = signal(false);
  /** Only direct applies get the offer; the generic form is already a matched application */
  canOfferMatch = computed(() => !!this.matchedLeadsProduct() || !!this.jobPositionId());
  private lastPayload: TdusaPartnerLead | null = null;

  private fb = inject(FormBuilder);
  private http = inject(HttpClient);
  private snackBar = inject(MatSnackBar);
  private platformId = inject(PLATFORM_ID);
  private destroyRef = inject(DestroyRef);

  form!: FormGroup;

  cdlClassChecked: Record<string, boolean> = {};
  endorsementChecked: Record<string, boolean> = {};
  interestedFreightTypeChecked: Record<string, boolean> = {};

  /** Avoid re-posting the same contact payload while the user keeps typing. */
  private lastPartialLeadKey = '';

  // Add these lookup maps near the top of the component, alongside the constants
  private readonly DRIVER_TYPE_ID: Record<string, string> = {
    'Lease Purchase': '1',
    'Company Driver': '2',
    'Owner Operator': '3',
  };

  private readonly CDL_CLASS_ID: Record<string, string> = {
    'Class A': '1',
    'Class B': '2',
    'Class C': '3',
  };

  ngOnInit(): void {
    CDL_CLASSES.forEach((c) => (this.cdlClassChecked[c] = false));
    ENDORSEMENTS.forEach((e) => (this.endorsementChecked[e] = false));
    FREIGHT_TYPES.forEach(
      (f) => (this.interestedFreightTypeChecked[f] = false)
    );

    this.cdlClassChecked['Class A'] = true;

    this.form = this.fb.group({
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      phone: ['', [Validators.required, usPhoneE164Validator()]],
      zipCode: [
        '',
        [Validators.required, Validators.pattern(/^\d{5}(-\d{4})?$/)],
      ],
      experienceType: [EXPERIENCE_OPTIONS[0], [Validators.required]],
      militaryExperience: ['NO', [Validators.required]],
      violations: [0],
      accidents: [0],
      driverType: ['Company Driver', [Validators.required]],
      interestInOoLp: ['NO'],
      hasOwnAuthority: ['Power Unit Only'],
      soloOrTeam: ['Solo Driver'],
      interestInTeamDriving: ['No'],
      freightType: [FREIGHT_TYPES[0]],
      applicationContactTime: [CONTACT_TIMES[0]],
      privacyPolicy: [false, [Validators.requiredTrue]],
      marketingMessages: [false, [Validators.requiredTrue]],
    });

    this.form.get('phone')!.valueChanges
  .pipe(debounceTime(150))
  .subscribe((val: string) => {
    if (!val) return;

    const digits = val.replace(/\D/g, '');

    if (digits.length === 11 && digits.startsWith('1')) {
      const stripped = digits.slice(1);
      this.form.get('phone')!.setValue(stripped, { emitEvent: false });
    }
  });

    

    // Restore saved profile last, after form exists
    this.restoreProfileFromStorage();
    this.watchPartialLeadFields();
  }

  // Partial lead capture
  private watchPartialLeadFields(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const contactControls = ['firstName', 'lastName', 'email', 'phone'] as const;

    merge(...contactControls.map((name) => this.form.get(name)!.valueChanges))
      .pipe(debounceTime(800), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.maybeSendPartialLead());
  }

  private maybeSendPartialLead(): void {
    if (this.submitted() || this.submitting()) return;

    const firstName = this.form.get('firstName');
    const lastName = this.form.get('lastName');
    const email = this.form.get('email');
    const phone = this.form.get('phone');

    if (
      !firstName?.valid ||
      !lastName?.valid ||
      !email?.valid ||
      !phone?.valid
    ) {
      return;
    }

    const payload: HomePartialLeadDto = {
      firstName: firstName.value,
      lastName: lastName.value,
      email: email.value,
      phone: new PhoneFormatters(phone.value).toE164US(),
    };

    const key = `${payload.firstName}|${payload.lastName}|${payload.email}|${payload.phone}`;
    if (key === this.lastPartialLeadKey) return;
    this.lastPartialLeadKey = key;

    this.http
      .post('/api/public/leads/partial-home', payload)
      .subscribe({ error: () => undefined });
  }

  // ── Visibility helpers ───────────────────────────────────────────────────

  get showEndorsements(): boolean {
    return this.cdlClassChecked['Class A'] === true;
  }

  get showInterestInOoLp(): boolean {
    return this.form.get('driverType')?.value === 'Company Driver';
  }

  get showHasOwnAuthority(): boolean {
    return this.form.get('driverType')?.value === 'Owner Operator';
  }

  get showInterestInTeamDriving(): boolean {
    return this.form.get('soloOrTeam')?.value !== 'Team Driver';
  }

  // ── Checkbox helpers ─────────────────────────────────────────────────────

  toggleCheck(map: Record<string, boolean>, key: string): void {
    map[key] = !map[key];
  }

  private checkedKeys(map: Record<string, boolean>): string {
    return Object.entries(map)
      .filter(([, v]) => v)
      .map(([k]) => k)
      .join(',');
  }

  // ── UTM ──────────────────────────────────────────────────────────────────

  private getUtm(): {
    utmSource: string;
    utmMedium: string;
    utmCampaign: string;
  } {
    return {
      utmSource: localStorage.getItem('utmSource') ?? 'tdusa',
      utmMedium: localStorage.getItem('utmMedium') ?? 'web',
      utmCampaign: localStorage.getItem('utmCampaign') ?? 'organic',
    };
  }

  // ── Qualifying jobs fetch ────────────────────────────────────────────────

  private fetchQualifyingJobs(): void {
    const v = this.form.value;
    const mlp = this.matchedLeadsProduct();

    this.loadingJobs.set(true);

    // Resolve driverType label → numeric ID
    const driverTypeId = this.DRIVER_TYPE_ID[v.driverType ?? ''] ?? '';

    // Prefer Class A if checked, otherwise take the first checked class → numeric ID
    const primaryCdlClass = this.cdlClassChecked['Class A']
      ? 'Class A'
      : (Object.entries(this.cdlClassChecked).find(
          ([, checked]) => checked
        )?.[0] ?? '');
    const cdlClassId = this.CDL_CLASS_ID[primaryCdlClass] ?? '';

    let params = new HttpParams().set('limit', '6').set('offset', '0');

    if (mlp?.slug) params = params.set('company', mlp.slug);
    if (driverTypeId) params = params.set('driverType', driverTypeId);
    if (cdlClassId) params = params.set('cdlClass', cdlClassId);
    if (v.zipCode) params = params.set('zip', v.zipCode);
    if (v.zipCode) params = params.set('radius', '50');

    this.http
      .get<JobSearchResultPublicDto>('/api/public/jobs', { params })
      .subscribe({
        next: (result) => {
          const filteredQualifyingJobs = (result.results ?? []).filter(
            (j) => j.id != this.jobPositionId()
          );
          this.qualifyingJobs.set(filteredQualifyingJobs);
          this.loadingJobs.set(false);
        },
        error: () => {
          this.loadingJobs.set(false);
        },
      });
  }

  // ── Submit ───────────────────────────────────────────────────────────────

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const v = this.form.value;
    const utm = this.getUtm();
    const mlp = this.matchedLeadsProduct();

    const payload: TdusaPartnerLead = {
      firstName: v.firstName,
      lastName: v.lastName,
      email: v.email,
      phone: new PhoneFormatters(v.phone).toE164US(),
      zip: v.zipCode,
      cdlClasses: this.checkedKeys(this.cdlClassChecked),
      endorsements: this.showEndorsements
        ? this.checkedKeys(this.endorsementChecked)
        : '',
      experienceType: v.experienceType,
      violations: v.violations,
      accidents: v.accidents,
      militaryExperience: v.militaryExperience === 'YES',
      driverType: v.driverType,
      teamDriver: v.soloOrTeam === 'Team Driver',
      interestInOoLp: this.showInterestInOoLp
        ? v.interestInOoLp === 'YES'
        : false,
      hasOwnAuthority: this.showHasOwnAuthority
        ? v.hasOwnAuthority === 'Own Authority'
        : false,
      interestInTeamDriving: this.showInterestInTeamDriving
        ? v.interestInTeamDriving === 'Yes'
        : false,
      freightType: v.freightType,
      interestedFreightTypes: this.checkedKeys(
        this.interestedFreightTypeChecked
      ),
      contactTime: v.applicationContactTime,
      utmSource: utm.utmSource,
      utmMedium: utm.utmMedium,
      utmCampaign: utm.utmCampaign,
      jobPositionId: this.jobPositionId() ?? '',
      // Resolve matchedLeadsProductId from the full object
      matchedLeadsProductId: mlp?.id ?? '',
    };

    // Persist profile before submitting
    this.saveProfileToStorage();
    this.lastPayload = payload;

    this.submitting.set(true);

    // Also post raw application to public services (UI still driven by admin response)
    this.http
      .post('/api/public/applications/raw', payload)
      .pipe(catchError(() => of(null)))
      .subscribe();

    this.http
      .post<TdusaPartnerLeadResponse>(
        '/api/admin/applications/external',
        payload
      )
      .subscribe({
        next: (response) => {
          this.submitting.set(false);
          if (response.success) {
            this.submitted.set(true);
          } else {
            this.disqualifiedMessage.set(
              response.message ?? 'You did not qualify for this position.'
            );
            this.disqualified.set(true);
            this.fetchQualifyingJobs();
          }
        },
        error: () => {
          this.submitting.set(false);
          this.snackBar.open(
            'Something went wrong. Please try again.',
            'Dismiss',
            { duration: 5000 }
          );
        },
      });
  }

  /**
   * One-click resubmit of the same application with no company or job, so it's processed as a
   * matched application (the same thing the generic "Get matched" form sends).
   */
  onMatchMe(): void {
    if (!this.lastPayload || this.matching() || this.matched()) return;

    const payload: TdusaPartnerLead = {
      ...this.lastPayload,
      jobPositionId: '',
      matchedLeadsProductId: '',
    };

    this.matching.set(true);

    this.http
      .post('/api/public/applications/raw', payload)
      .pipe(catchError(() => of(null)))
      .subscribe();

    this.http
      .post<TdusaPartnerLeadResponse>(
        '/api/admin/applications/external',
        payload
      )
      .subscribe({
        next: (response) => {
          this.matching.set(false);
          if (response.success) {
            this.matched.set(true);
          } else {
            this.snackBar.open(
              response.message ?? "We couldn't match you right now. Please try again.",
              'Dismiss',
              { duration: 5000 }
            );
          }
        },
        error: () => {
          this.matching.set(false);
          this.snackBar.open(
            'Something went wrong. Please try again.',
            'Dismiss',
            { duration: 5000 }
          );
        },
      });
  }

  //save/restore
  private readonly STORAGE_KEY = 'tdusa_driver_profile';

  private saveProfileToStorage(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const v = this.form.value;

    const profile = {
      // Personal info
      firstName: v.firstName,
      lastName: v.lastName,
      email: v.email,
      phone: v.phone,
      zipCode: v.zipCode,

      // Driver profile fields
      experienceType: v.experienceType,
      militaryExperience: v.militaryExperience,
      violations: v.violations,
      accidents: v.accidents,
      driverType: v.driverType,
      interestInOoLp: v.interestInOoLp,
      hasOwnAuthority: v.hasOwnAuthority,
      soloOrTeam: v.soloOrTeam,
      interestInTeamDriving: v.interestInTeamDriving,
      freightType: v.freightType,
      applicationContactTime: v.applicationContactTime,
      marketingMessages: v.marketingMessages,

      // Checkbox maps
      cdlClassChecked: this.cdlClassChecked,
      endorsementChecked: this.endorsementChecked,
      interestedFreightTypeChecked: this.interestedFreightTypeChecked,
    };

    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(profile));
  }

  private restoreProfileFromStorage(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    try {
      const raw = localStorage.getItem(this.STORAGE_KEY);
      if (!raw) return;

      const profile = JSON.parse(raw);

      // Patch form controls
      this.form.patchValue(
        {
          firstName: profile.firstName ?? '',
          lastName: profile.lastName ?? '',
          email: profile.email ?? '',
          phone: profile.phone
            ? new PhoneFormatters(profile.phone).toNationalDigits()
            : '',
          zipCode: profile.zipCode ?? '',
          experienceType: profile.experienceType ?? EXPERIENCE_OPTIONS[0],
          militaryExperience: profile.militaryExperience ?? 'NO',
          violations: profile.violations ?? 0,
          accidents: profile.accidents ?? 0,
          driverType: profile.driverType ?? 'Company Driver',
          interestInOoLp: profile.interestInOoLp ?? 'NO',
          hasOwnAuthority: profile.hasOwnAuthority ?? 'Power Unit Only',
          soloOrTeam: profile.soloOrTeam ?? 'Solo Driver',
          interestInTeamDriving: profile.interestInTeamDriving ?? 'No',
          freightType: profile.freightType ?? FREIGHT_TYPES[0],
          applicationContactTime:
            profile.applicationContactTime ?? CONTACT_TIMES[0],
          marketingMessages: profile.marketingMessages ?? true,
        },
        { emitEvent: false }
      );

      // Restore checkbox maps — guard each key against stale/unknown values
      if (profile.cdlClassChecked) {
        CDL_CLASSES.forEach((c) => {
          this.cdlClassChecked[c] = profile.cdlClassChecked[c] ?? false;
        });
      }

      if (profile.endorsementChecked) {
        ENDORSEMENTS.forEach((e) => {
          this.endorsementChecked[e] = profile.endorsementChecked[e] ?? false;
        });
      }

      if (profile.interestedFreightTypeChecked) {
        FREIGHT_TYPES.forEach((f) => {
          this.interestedFreightTypeChecked[f] =
            profile.interestedFreightTypeChecked[f] ?? false;
        });
      }
    } catch {
      // Corrupted storage — silently ignore and use defaults
      localStorage.removeItem(this.STORAGE_KEY);
    }
  }

  onPhoneInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let digits = input.value.replace(/\D/g, '');
  
    // Strip leading country code before formatting
    if (digits.length === 11 && digits.startsWith('1')) {
      digits = digits.slice(1);
    }
  
    digits = digits.slice(0, 10);
  
    // Format as (XXX) XXX-XXXX
    let formatted = digits;
    if (digits.length >= 7) {
      formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    } else if (digits.length >= 4) {
      formatted = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else if (digits.length >= 1) {
      formatted = `(${digits}`;
    }
  
    // Set the native input value for display
    input.value = formatted;
  
    // Patch the raw digits into the form control for validation
    this.form.get('phone')!.setValue(digits, { emitEvent: false });
    this.form.get('phone')!.markAsTouched();
  }
}
